import type { AlertPosterComponent } from './AlertPoster.d';
import { Row as LeftToRight, Stack as TopToBottom } from '/react4xp/components/Flex';
import React from 'react';
import { isString } from '@enonic/js-utils/value/isString';
import {
	faEye,
	faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";

import { safeHtmlDecode } from '/react4xp/util/safeHtmlDecode';
import { DateComponent } from '../../components/DateComponent';
import Richtext from '../Richtext/Richtext';
import { QR } from './QR';
import { ODFLogo } from './ODFLogo';
import { CircleChevronRight } from './CircleChevronRight';
import { Radar } from './Radar';

// Functional component to wrap each <li> element
function ListItem({ children }: { children: React.ReactNode; }): JSX.Element {
	return (
		<LeftToRight as='li' className='gap-[8px] items-start'>
			{/* (line-height of text 21 - icon height 15) / 2 = margin-top of icon 3 */}
			<CircleChevronRight className='mt-[3px] shrink-0'/>
			{children}
		</LeftToRight>
	);
}

// Helper function to convert DOM nodes to React elements
function domToReact(node: Node | Node[], key?: React.Key): JSX.Element | string | null {
	if (Array.isArray(node)) {
		return (
			<>
				{node.map((n, i) => (
					<React.Fragment key={i}>{domToReact(n, i)}</React.Fragment>
				))}
			</>
		);
	}

	if (node.nodeType === Node.TEXT_NODE) {
		return (node as Text).textContent;
	}
	if (node.nodeType !== Node.ELEMENT_NODE) {
		return null;
	}
	const element = node as Element;
	const tag = element.tagName.toLowerCase();
	const props: Record<string,string> = {};
	for (const attr of Array.from(element.attributes)) {
		props[attr.name] = attr.value;
	}

	// If the node is an <li>, wrap it in the ListItem component
	if (tag === 'li') {
		return (
			<ListItem key={key}>
				<span dangerouslySetInnerHTML={{ __html: element.innerHTML }} />
			</ListItem>
		);
	}

	// Convert child nodes recursively
	const children = Array
		.from(node.childNodes)
		.map((child, childIndex) =>
			domToReact(child, `parentIndex_${key}_childIndex_${childIndex}`),
		)
		.filter(child => child !== null);

	if (tag === 'ul') {
		return (<TopToBottom as='ul' className='gap-[8px] list-none m-0 p-0' key={key}>{children}</TopToBottom>);
	}

	// include the key (after the spread so an HTML "key" attribute can't clobber it)
	return React.createElement(tag, { ...props, key }, children.length ? children : null);
}

// NOTE: Since this is only used for printing, try to stay away from using tailwind classes whoose
// values can change like xxs...xxl.
export const AlertPoster: AlertPosterComponent = ({
	appearance,
	descriptionHtml,
	expiresAt,
	imgSrc,
	onImageLoad = () => { /* no-op */ },
	published,
	recommendationHtml,
	title,
}) => {
	// Lets try to convert Figma pixel dimensions to percent:

	// Horizontal:
	// Left Red Column: 382px (including 30px padding)
	// + Right Beige Column: 460px (including 25px padding)
	// = Total: 842px
	// 460/842=0,54631829
	// 382/842=0,45368171
	const w125 = 'w-[14.845606vw]';
	// 45/842=5.344418vw
	const px30 = 'px-[3.562945vw]';
	// 24/842=2.850356vw
	// 12/842=1.425178vw
	// 8/842=0.950119vw

	// Vertical:
	// Red column 510px (including 30px padding)
	// + QA Area 85px
	// = Total 595px
	// 510/595=0,85714286
	// 85/595=0,14285717
	const text56 = 'text-[9.411765vh]';
	// 45/595=7.563025vh
	// 36/595=6.05042vh
	const text36 = 'text-[6.05042vh]';
	const py30 = 'py-[5.042017vh]';
	const text24 = 'text-[4.033613vh]';
	const gap16 = 'gap-[2.689076vh]';
	const text16 = 'text-[2.689076vh]';
	const text14 = 'text-[2.352941vh]';
	const text12 = 'text-[2.016807vh]';
	const text10 = 'text-[1.680672vh]';
	// 8/595=1.344538vh

	// NOTE: DOMParser only exists in the Browser, not in Graal.
	const recommendation = typeof document === 'undefined' ? null : (() => {
		// Parse recommendationHtml synchronously
		// Using useEffect fails when printing iframe.
		const parser = new DOMParser();
		const doc = parser.parseFromString(recommendationHtml, 'text/html');
		const nodes = Array.from(doc.body.childNodes);
		return nodes.length > 0 ? domToReact(nodes) : null;
	})();

	return (
		// relative to position Radar absolutely.
		<LeftToRight className='relative w-full'>

			<Radar className='absolute right-0'/>

			<TopToBottom className='grow'>
				<TopToBottom className={`bg-[#E30713] ${gap16} h-[85.714286vh] ${px30} ${py30} rounded-br-[10px] shrink-0 text-white w-[45.368171vw]`}>
					<LeftToRight className={`align-middle justify-between ${text56}`}>
						<Icon height={56} icon={faTriangleExclamation}/>
						<ODFLogo className='self-center'/>
					</LeftToRight>

					{/*
						The widest character in the Roboto font is uppercase W.
						On 3 lines with font-size approximating 36px, there is room for only 30 W's.
						On 3 lines with font-size approximating 24px, there is room for only 45 W's.
						On 3 lines with font-size approximating 16px, there is room for only 69 W's.
						On 3 lines with font-size approximating 14px, there is room for only 78 W's.
						On 3 lines with font-size approximating 12px, there is room for only 93 W's.
						On 3 lines with font-size approximating 10px, there is room for only 111 W's.
						Using the above gave too small font for normal titles.

						Tried to get a fake sentence with a standard distribution of Norwegian
						characters and uppercase letters, digits, punctucation and word lengths:
						"Enare vølst, Nidra kåse, tildre: Sørna elgta vinåst drenke låvti. Snøre Kalti gørne Stida værke nalta Drøme kesti vånse tørla gisna eldra Kåvne snati lødre Velta nørke sidra tåvne keldre sørti vælna, 3 25."

						On 3 lines with font-size approximating 36px, there is room for only 56 characters.
						On 3 lines with font-size approximating 24px, there is room for only 93 characters.
						On 3 lines with font-size approximating 16px, there is room for only 129 characters.
						On 3 lines with font-size approximating 14px, there is room for only 153 characters.
						On 3 lines with font-size approximating 12px, there is room for only 184 characters.
						On 3 lines with font-size approximating 10px, there is room for more than the test string.
					*/}
					<LeftToRight
						as='h1'
						className={`font-bold ${
							title.length > 184 ? text10
								: title.length > 153 ? text12
									: title.length > 129 ? text14
										: title.length > 93 ? text16
											: title.length > 56 ? text24
												: text36
						}`
						}
					>{safeHtmlDecode(title)}</LeftToRight>

					{/* Since descriptionHtml is not just simple text, the point at which font-size changes must be guessimated */}
					<Richtext
						className={`font-semibold ${
							descriptionHtml.length > 650
								? text10
								: descriptionHtml.length > 500
									? text12
									: text14
						}`}
						text={descriptionHtml}
					/>
				</TopToBottom>
				<LeftToRight className='gap-[2.850356vw] grow-0 h-[14.285717vh] pl-[1.425178vw] pr-[3.562945vw] py-[2.016807vh]'>
					<LeftToRight className='border-[1px] border-[#BDBDBD] border-solid gap-[0.950119vw] px-[0.950119vw] py-[1.344538vh] rounded-[4px]'>
						<QR height='7.563025vh' width='5.344418vw'/>
						<TopToBottom className={`justify-center ${text12}`}>
							<span>Scan for å gå til</span>
							<span>overdosefare.no</span>
						</TopToBottom>
					</LeftToRight>
					<TopToBottom className={`justify-center ${text12} text-[#504F4F]`}>
						<span className='font-bold'>Varselet er gyldig fra:</span>
						<span className='text-nowrap'>
							<DateComponent dateTime={published} format='D. MMMM'/>
							-
							<DateComponent dateTime={expiresAt} format='D. MMMM YYYY'/>
						</span>
					</TopToBottom>
				</LeftToRight>
			</TopToBottom>

			{/* z-10 to make Radar appear below it */}
			<TopToBottom className='gap-[25px] grow pb-[12px] pt-[30px] px-[25px] w-[54.631829vw] z-10'>
				<TopToBottom className='bg-[#F8F5F0] gap-[8px] rounded-[8px] p-[20px] '>
					<LeftToRight as='h2' className={`font-semibold gap-[8px] items-center ${text16}`}>
						{
							imgSrc
								? null
								: <Icon
									height='2.689076vh'
									icon={faEye}
								/>
						}
						Utseende
					</LeftToRight>
					<LeftToRight className='gap-[1.425178vw]'>
						{/* This image does not show up in the print preview dialog */}
						{
							imgSrc
								? <figure className={`aspect-[125/138] overflow-hidden rounded-[5.236px] shrink-0 ${w125}`}>
									<img className={`h-full object-center object-cover w-full`} onLoad={onImageLoad} src={imgSrc}/>
								</figure>
								: null
						}

						{/* appearance can contain htmlEncoded chars */}
						<span
							className={`shrink ${
								appearance.length > 400
									? text12
									: text16
							}`}
						>{safeHtmlDecode(appearance)}</span>
					</LeftToRight>
				</TopToBottom>
				<TopToBottom className='bg-[#F8F5F0] gap-[8px] rounded-[8px] p-[20px]'>
					<LeftToRight as='h2' className={`font-semibold ${text16}`}>Anbefalinger og forholdsregler</LeftToRight>

					{/* Since recommendation is not just simple text, the point at which font-size changes must be guessimated */}
					<TopToBottom className={`leading-[21px] ${isString(recommendation) && recommendation.length > 400 ? text12 : text14}`}>
						{recommendation}
					</TopToBottom>
				</TopToBottom>
			</TopToBottom>

		</LeftToRight>
	);
};
